/**
 * Founder-facing vs technical/internal question presentation for Feature Handoff.
 * Does not change backend question state — display/partition only.
 */

const TECHNICAL_MARKERS = [
  /non-contractual clarification/i,
  /\bsource clause[- ]?\d+/i,
  /\bsource clause-/i,
  /verified quotation/i,
  /\bskip reason\b/i,
  /\bnot_requirement_like\b/i,
  /\bdraft criterion used\b/i,
  /\bbroadened or paraphrased\b/i,
  /\bactor\s*=/i,
  /\baction\s*=/i,
  /\bexpectedoutcomes?\s*=/i,
  /\bcriterionid\b/i,
  /\bsourceclauseids?\b/i,
  /should an exact source statement become/i,
  /should the exact source statement become/i,
  /confirm this as a (?:non-goal|assumption|protected behavior)/i,
  /exact actor, action, and measurable outcome/i,
  /split into a shorter exact requirement/i,
];

const ACTOR_ALREADY_CLEAR_RE =
  /\b(fastify|express|nestjs|users?|admins?|customers?|operators?|members?|the (?:system|library|service|api|server))\b/i;

/**
 * True when the question text is containment/scaffold/debug language, not a product clarify ask.
 */
export function isTechnicalInternalQuestion(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  return TECHNICAL_MARKERS.some((re) => re.test(t));
}

/**
 * "Who is the actor?" style asks are noise when the request already names one.
 */
export function isRedundantActorQuestion(text, requestText = "") {
  const t = String(text || "");
  const asksWhoIsActor =
    /\bwho (?:is|are)(?:\s+the)?\s+actor\b/i.test(t) ||
    /\bwho(?:'s| is) the actor\b/i.test(t);
  if (!asksWhoIsActor) return false;
  // Internal scaffold often appends "Who is the actor?" as a hint.
  if (isTechnicalInternalQuestion(t)) return true;
  return ACTOR_ALREADY_CLEAR_RE.test(String(requestText || ""));
}

/**
 * Soft normalize of a server question into founder-facing copy when we keep it visible.
 * Returns null if it should not appear in the founder list.
 */
export function normalizeFounderFacingQuestionText(text, requestText = "") {
  const raw = cleanClarificationPart(text);
  if (!raw) return null;
  if (isTechnicalInternalQuestion(raw)) {
    // Prefer hiding; only keep a soft fallback when nothing else covers it.
    if (/timeout|too long|forever|slow request/i.test(raw + requestText)) {
      return "Confirm the exact requirement wording for this timeout behavior.";
    }
    return "Confirm the exact requirement wording you want in this handoff.";
  }
  if (isRedundantActorQuestion(raw, requestText)) return null;
  return raw;
}

export function founderQuestionCategoryLabel(category) {
  switch (category) {
    case "unsupported_claim":
      return "Needs clarification";
    case "missing_actor":
      return "Who does this?";
    case "missing_condition":
      return "When does this apply?";
    case "missing_outcome":
      return "What should success look like?";
    case "missing_failure_behavior":
      return "What happens when it fails?";
    case "missing_threshold":
      return "What number or limit?";
    case "missing_timeframe":
      return "By when / how long?";
    case "missing_non_goal":
      return "What is out of scope?";
    case "missing_validation":
      return "How will we prove it works?";
    case "missing_rollout":
      return "How should this roll out?";
    case "missing_data_handling":
      return "How should data and access work?";
    case "ambiguous_scope":
      return "Scope unclear";
    default:
      return "Needs clarification";
  }
}

function normKey(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Parse a clarification note saved from the Open questions UI.
 * @returns {{ kind: 'answered'|'not_required', question: string, response: string }|null}
 */
function cleanClarificationPart(text) {
  let out = String(text || "");
  for (let i = 0; i < 8; i++) {
    const next = out
      .replace(/\\+([.!?,;:'"”’)\]}])/g, "$1")
      .replace(/\\{2,}/g, "")
      .replace(/\\+$/g, "");
    if (next === out) break;
    out = next;
  }
  return out.replace(/\s+/g, " ").trim();
}

export function parseClarificationNote(text) {
  const t = String(text || "").trim();
  if (!t) return null;
  let m = t.match(/^Clarification — (.+?) Answer: ([\s\S]+)$/i);
  if (m) {
    return {
      kind: "answered",
      question: cleanClarificationPart(m[1]),
      response: cleanClarificationPart(m[2]),
    };
  }
  m = t.match(/^Clarification not required — (.+?) Reason: ([\s\S]+)$/i);
  if (m) {
    return {
      kind: "not_required",
      question: cleanClarificationPart(m[1]),
      response: cleanClarificationPart(m[2]),
    };
  }
  return null;
}

/**
 * Index human notes that are clarification Q&A (by normalized question text).
 * @param {Array<{ assumptionId?: string, text?: string }>} notes
 */
export function indexClarificationNotes(notes = []) {
  const map = new Map();
  for (const n of Array.isArray(notes) ? notes : []) {
    const parsed = parseClarificationNote(n?.text);
    if (!parsed) continue;
    map.set(normKey(parsed.question), {
      ...parsed,
      assumptionId: n.assumptionId,
      rawText: String(n.text || ""),
    });
  }
  return map;
}

/**
 * Build founder-facing open-question cards + technical/internal list.
 *
 * @param {{
 *   serverQuestions?: Array<{ questionId?: string, text?: string, status?: string, category?: string, severity?: string }>,
 *   guidanceQuestions?: string[],
 *   requestText?: string,
 *   keepTechnicalFallbackInFounderList?: boolean,
 *   clarificationNotes?: Array<{ assumptionId?: string, text?: string }>,
 * }} input
 */
export function partitionHandoffQuestions(input = {}) {
  const serverQuestions = Array.isArray(input.serverQuestions)
    ? input.serverQuestions
    : [];
  const guidanceQuestions = Array.isArray(input.guidanceQuestions)
    ? input.guidanceQuestions
    : [];
  const requestText = String(input.requestText || "");
  const keepFallback = Boolean(input.keepTechnicalFallbackInFounderList);
  const savedByQuestion = indexClarificationNotes(input.clarificationNotes);

  const technical = [];
  const founderFromServer = [];
  const seen = new Set();

  for (const q of serverQuestions) {
    const text = String(q.text || "").trim();
    if (!text) continue;
    const technicalHit =
      isTechnicalInternalQuestion(text) || isRedundantActorQuestion(text, requestText);
    if (technicalHit) {
      technical.push({
        ...q,
        displayKind: "technical",
        displayText: text,
      });
      continue;
    }
    const displayText = normalizeFounderFacingQuestionText(text, requestText);
    if (!displayText) {
      technical.push({
        ...q,
        displayKind: "technical",
        displayText: text,
      });
      continue;
    }
    const key = normKey(displayText);
    if (seen.has(key)) continue;
    seen.add(key);
    founderFromServer.push({
      ...q,
      displayKind: "server",
      displayText,
      resolvable: true,
    });
  }

  const founder = [];
  const savedGuidance = [];
  for (const g of guidanceQuestions) {
    const text = String(g || "").trim();
    if (!text) continue;
    const key = normKey(text);
    if (seen.has(key)) continue;
    seen.add(key);
    const saved = savedByQuestion.get(key);
    if (saved) {
      savedGuidance.push({
        questionId: null,
        guidanceKey: `guidance:${key.slice(0, 80)}`,
        text,
        displayText: text,
        displayKind: "guidance",
        resolvable: false,
        status: saved.kind === "not_required" ? "not_required" : "answered",
        category: "other",
        severity: "high",
        savedResponse: saved.response,
        savedKind: saved.kind,
        assumptionId: saved.assumptionId,
      });
      continue;
    }
    founder.push({
      questionId: null,
      guidanceKey: `guidance:${key.slice(0, 80)}`,
      text,
      displayText: text,
      displayKind: "guidance",
      resolvable: false,
      status: "open",
      category: "other",
      severity: "high",
    });
  }
  for (const q of founderFromServer) {
    founder.push(q);
  }
  founder.sort(
    (a, b) =>
      (Number(b.valueScore ?? b.priority) || 0) -
      (Number(a.valueScore ?? a.priority) || 0)
  );

  // If we hid everything and have only technical questions, optionally surface one soft prompt
  // when there is no guidance — avoids an empty list that looks like "no questions".
  if (
    keepFallback &&
    founder.length === 0 &&
    technical.length > 0 &&
    guidanceQuestions.length === 0
  ) {
    founder.push({
      questionId: null,
      guidanceKey: "guidance:confirm-exact-wording",
      text: "Confirm the exact requirement wording you want in this handoff.",
      displayText: "Confirm the exact requirement wording you want in this handoff.",
      displayKind: "guidance",
      resolvable: false,
      status: "open",
      category: "other",
      severity: "medium",
    });
  }

  return {
    founderQuestions: founder,
    savedGuidanceQuestions: savedGuidance,
    technicalQuestions: technical,
    technicalOpenCount: technical.length,
    founderOpenCount: founder.length,
    savedGuidanceCount: savedGuidance.length,
  };
}
